import type { BirdEyeBoardState, BirdEyeDragSource, BirdEyeDropTarget } from './boardTypes';
import type { BirdEyePatient } from './types';

function getPatient(state: BirdEyeBoardState, from: BirdEyeDragSource): BirdEyePatient | null {
  if (from.type === 'list') {
    return state[from.lane].find((p) => p.id === from.patientId) ?? null;
  }
  return state.ops[from.index]?.seated ?? null;
}

function isSameTarget(from: BirdEyeDragSource, to: BirdEyeDropTarget): boolean {
  if (from.type === 'operatory' && to.type === 'operatory') return from.index === to.index;
  if (from.type === 'list' && to.type === 'list') return from.lane === to.lane;
  return false;
}

function removePatient(state: BirdEyeBoardState, from: BirdEyeDragSource): BirdEyeBoardState {
  if (from.type === 'list') {
    return { ...state, [from.lane]: state[from.lane].filter((p) => p.id !== from.patientId) };
  }
  return {
    ...state,
    ops: state.ops.map((op, i) => (i === from.index ? { ...op, seated: null } : op)),
  };
}

function insertPatient(
  state: BirdEyeBoardState,
  to: BirdEyeDropTarget,
  patient: BirdEyePatient,
  from: BirdEyeDragSource,
): BirdEyeBoardState {
  if (to.type === 'list') {
    return { ...state, [to.lane]: [...state[to.lane], patient] };
  }

  const ops = state.ops.map((op) => ({ ...op, seated: op.seated ? { ...op.seated } : null }));
  const op = ops[to.index];
  if (!op) return state;

  const displaced = op.seated;
  ops[to.index] = { ...op, seated: patient };

  if (!displaced) return { ...state, ops };

  if (from.type === 'list') {
    return { ...state, [from.lane]: [...state[from.lane], displaced], ops };
  }
  if (from.type === 'operatory') {
    const fromOp = ops[from.index];
    if (fromOp) ops[from.index] = { room: fromOp.room, seated: displaced };
    return { ...state, ops };
  }

  return { ...state, ops };
}

export function movePatientInBoard(
  state: BirdEyeBoardState,
  from: BirdEyeDragSource,
  to: BirdEyeDropTarget,
): BirdEyeBoardState {
  const patient = getPatient(state, from);
  if (!patient || isSameTarget(from, to)) return state;

  const without = removePatient(state, from);
  return insertPatient(without, to, patient, from);
}
